// CE-04g-UI-pivots-iseriesprimitive-refactor — one ISeriesPrimitive per
// pivot preset, attached to the candle series via attachPrimitive. Paints
// pivot lines AND inline pill labels in a single canvas pass via
// IPrimitivePaneRenderer.draw, eliminating the v0.24.14 1–2-frame pill
// lag during price-axis scaling. The primitive never adds data points to
// any series, so pivot prices cannot enter the candle-series autoscale.

import {
  buildPivotTimelineSegmentData,
  withAlpha,
  formatPrice,
} from './pivotOverlayRenderer.js'

// LWC LineStyle enum -> canvas dash array. Used as a fallback when the
// renderer's `utils.setLineStyle` is not supplied at draw time.
const LINE_DASH_BY_STYLE = {
  0: [],          // Solid
  1: [1, 2],      // Dotted
  2: [4, 4],      // Dashed
  3: [8, 4],      // LargeDashed
  4: [1, 5],      // SparseDotted
}

// CE-04g-UI-pivots-tv-fidelity-fix — visible gap (CSS px) between the
// segment's right endpoint and the inline pill label. Carry-forward from
// pivotOverlayRenderer's pill positioning.
const PIVOT_LABEL_GAP_PX = 12
const DEFAULT_SYNTHETIC_BAR_SPACING_PX = 6

// Iteration order for the 7 display keys (matches v0.24.14's
// PIVOT_DISPLAY_KEYS in TradeChart.jsx). Missing entries = component
// hidden via Style tab; the renderer skips them.
const PIVOT_DISPLAY_KEYS = ['P', 'R1', 'R2', 'R3', 'S1', 'S2', 'S3']

function readBarSpacing(timeScale) {
  try {
    const spacing = timeScale?.options?.()?.barSpacing
    if (Number.isFinite(spacing) && spacing > 0) return spacing
  } catch { /* noop */ }
  return DEFAULT_SYNTHETIC_BAR_SPACING_PX
}

function resolveSegmentPanePoints({ segmentData, timeScale, series, horizontalPixelRatio, verticalPixelRatio }) {
  const points = []
  let lastX = null
  let syntheticStep = readBarSpacing(timeScale)

  for (const p of segmentData) {
    let xLogical = timeScale.timeToCoordinate(p.time)
    if (xLogical == null) {
      if (lastX == null) continue
      xLogical = lastX + syntheticStep
    } else if (lastX != null && xLogical > lastX) {
      syntheticStep = xLogical - lastX
    }
    const yLogical = series.priceToCoordinate(p.value)
    if (yLogical == null) continue
    lastX = xLogical
    points.push({
      x: xLogical * horizontalPixelRatio,
      y: yLogical * verticalPixelRatio,
    })
  }

  return points
}

export class PivotPrimitive {
  constructor() {
    this._data = null
    this._chart = null
    this._series = null
    this._requestUpdate = null
    // Stable single-element pane-views array — LWC caches the views array
    // by reference, so returning a fresh array each call would invalidate
    // the cache and force unnecessary redraws.
    this._paneViews = [new PivotPaneView(this)]
  }

  attached(param) {
    this._chart = param.chart
    this._series = param.series
    this._requestUpdate = param.requestUpdate
  }

  detached() {
    this._chart = null
    this._series = null
    this._requestUpdate = null
    this._data = null
  }

  update(next) {
    this._data = next
    if (typeof this._requestUpdate === 'function') {
      this._requestUpdate()
    }
  }

  updateAllViews() { /* renderer reads live state */ }

  paneViews() {
    return this._paneViews
  }
}

class PivotPaneView {
  constructor(primitive) {
    this._primitive = primitive
    this._renderer = new PivotPaneRenderer(primitive)
  }
  zOrder() { return 'bottom' }
  renderer() { return this._renderer }
}

class PivotPaneRenderer {
  constructor(primitive) {
    this._primitive = primitive
  }

  // LWC v5 signature: draw(target, utils). `utils` is optional; when
  // present and exposing setLineStyle(ctx, lineStyleEnum), prefer it over
  // the hardcoded dash arrays so dashed/dotted/sparse render
  // byte-identical to LWC line-series rendering.
  draw(target, utils) {
    const data = this._primitive._data
    const chart = this._primitive._chart
    const series = this._primitive._series
    if (!data || !chart || !series) return

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context
      const timeScale = chart.timeScale()

      for (const dk of PIVOT_DISPLAY_KEYS) {
        const level = data.levels?.[dk]
        if (!level) continue
        if (!Number.isFinite(level.price)) continue

        const segmentData = buildPivotTimelineSegmentData({
          sessionStartTime: data.segmentStartTime,
          currentRthCloseTime: data.currentRthCloseTime,
          segmentEndTime: data.segmentEndTime,
          price: level.price,
          effectiveBars: data.effectiveBars,
        })
        if (!segmentData || segmentData.length < 2) continue

        const points = resolveSegmentPanePoints({
          segmentData,
          timeScale,
          series,
          horizontalPixelRatio: scope.horizontalPixelRatio,
          verticalPixelRatio: scope.verticalPixelRatio,
        })
        if (points.length < 2) continue

        ctx.save()
        ctx.strokeStyle = level.color
        ctx.lineWidth = (level.lineWidth ?? 1) * scope.verticalPixelRatio
        if (utils && typeof utils.setLineStyle === 'function') {
          utils.setLineStyle(ctx, level.lineStyleEnum)
        } else {
          const dash = LINE_DASH_BY_STYLE[level.lineStyleEnum] ?? []
          ctx.setLineDash(dash.map((d) => d * scope.horizontalPixelRatio))
        }
        ctx.beginPath()
        ctx.moveTo(points[0].x, points[0].y)
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y)
        }
        ctx.stroke()
        ctx.restore()

        if (level.axisLabelVisible === false) continue
        const endpoint = points[points.length - 1]
        const labelText = `${dk} (${formatPrice(level.price, data.priceScaleMinMove)})`
        ctx.save()
        // Preserve v0.24.14 typography: fontWeight 500, ui-monospace
        // stack, text-shadow that the DOM pills carried.
        const fontSize = (data.fontSize ?? 11) * scope.verticalPixelRatio
        ctx.font = `500 ${fontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`
        ctx.textBaseline = 'middle'
        ctx.fillStyle = withAlpha(level.color, 0.72)
        ctx.shadowColor = 'rgba(0, 0, 0, 0.65)'
        ctx.shadowOffsetX = 0
        ctx.shadowOffsetY = 1 * scope.verticalPixelRatio
        ctx.shadowBlur = 1 * scope.verticalPixelRatio
        ctx.fillText(
          labelText,
          endpoint.x + (PIVOT_LABEL_GAP_PX * scope.horizontalPixelRatio),
          endpoint.y,
        )
        ctx.restore()
      }
    })
  }
}
